/**
 * Bottom Nav fixa — 8ª ilha.
 *
 * Paridade com V1 (`#bottom-nav-bar`, delivery_app.html:1126).
 * 4 botões: Home · Loja · Sacola · Conta.
 * Badge da sacola = count do carrinho (reativo a `aliv:cart-updated`).
 *
 * Handlers:
 *  - Home: `goBackToHome()` (V1) ou scrollTo(0)
 *  - Loja: `openStoreInfo()` (V1 — modal com horários/endereço/mapa).
 *    Próxima iteração: migrar pra StoreInfoIsland V2.
 *  - Sacola: `openCartModal()` — abre CartModalIsland V2 via shim
 *  - Conta: `openUserArea('orders')` — abre UserAreaIsland V2 já na
 *    tab Pedidos (mais útil que Dados pra cliente recorrente).
 *    A própria UserArea tem as 3 tabs (Pedidos | Endereços | Dados).
 *
 * Padrão big tech (iFood/Rappi): safe-area-inset-bottom, tabular-nums
 * no badge, active:scale 0.95 no tap.
 */
import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import type { CartLine } from '../types';
import { useAnyModalOpen } from '../hooks/useAnyModalOpen';
import { t, useI18n } from '../i18n';

declare global {
  interface Window {
    CART?: CartLine[];
    goBackToHome?: () => void;
    openCartModal?: () => void;
    openUserArea?: (tab?: string) => void;
    openStoreInfo?: () => void;
  }
}

function subscribe(cb: () => void) {
  window.addEventListener('aliv:cart-updated', cb);
  return () => window.removeEventListener('aliv:cart-updated', cb);
}

interface CartSnapshot {
  count: number;
  total: number;
}

function getCartSnapshot(): CartSnapshot {
  const cart = window.CART || [];
  let count = 0;
  let total = 0;
  for (const it of cart) {
    const q = Number(it.quantity) || 0;
    count += q;
    total += (Number(it.price) || 0) * q;
  }
  return { count, total };
}

// useSyncExternalStore exige getSnapshot estável quando os dados não
// mudam (mesma referência). Cache o último snapshot e só devolve um novo
// quando algum campo divergir.
let lastSnap: CartSnapshot = { count: -1, total: -1 };
function getStableSnapshot(): CartSnapshot {
  const s = getCartSnapshot();
  if (s.count !== lastSnap.count || s.total !== lastSnap.total) {
    lastSnap = s;
  }
  return lastSnap;
}

// Toast simples renderizado direto no body — feedback pra cliente
// que clicou em "Sacola" estando vazia. UX iFood: mensagem rápida +
// fade-out 2.5s. Sem libs (manter bundle pequeno).
function _showEmptyCartToast() {
  let toast = document.getElementById('aliv-cart-empty-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'aliv-cart-empty-toast';
    toast.style.cssText = [
      'position:fixed',
      'bottom:96px',
      'left:50%',
      'transform:translateX(-50%)',
      'background:rgba(17,24,39,.96)',
      'color:#fff',
      'padding:12px 20px',
      'border-radius:9999px',
      'font-size:13px',
      'font-weight:600',
      'z-index:100050',
      'box-shadow:0 8px 24px rgba(0,0,0,.3)',
      'opacity:0',
      'transition:opacity .2s ease',
      'display:flex',
      'align-items:center',
      'gap:8px',
      'max-width:calc(100% - 32px)',
      'text-align:center',
    ].join(';');
    toast.innerHTML = '<i class="fas fa-shopping-bag"></i><span>'
      + t('Sua sacola está vazia. Escolha algum produto pra começar!') + '</span>';
    document.body.appendChild(toast);
  }
  requestAnimationFrame(() => { toast!.style.opacity = '1'; });
  const tWithTimer = toast as HTMLElement & { _hideTimer?: number };
  clearTimeout(tWithTimer._hideTimer);
  tWithTimer._hideTimer = window.setTimeout(() => {
    if (toast) toast.style.opacity = '0';
  }, 2500);
}

function fmtBRL(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface NavButtonProps {
  label: string;
  icon: string;
  onClick: () => void;
  badge?: number;
  active?: boolean;
  /** Destaque pulsante (Sacola com itens). */
  highlight?: boolean;
  /** Sub-texto pequeno abaixo do label (ex: valor total no Sacola). */
  subText?: string;
}

function NavButton({ label, icon, onClick, badge, active, highlight, subText }: NavButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex flex-col items-center gap-0.5 px-3 py-1.5 transition active:scale-95 relative',
        highlight
          ? 'text-red-600'
          : active
          ? 'text-red-600'
          : 'text-gray-400 hover:text-gray-600',
      ].join(' ')}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <div className="relative">
        <i
          className={[
            'fas',
            icon,
            highlight ? 'text-xl animate-pulse' : 'text-lg',
            highlight ? 'drop-shadow-sm' : '',
          ].join(' ')}
        />
        {!!badge && badge > 0 && (
          <span className="absolute -top-1.5 -right-2 bg-red-600 text-white text-[9px] font-extrabold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 tabular-nums animate-pulse">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span
        className={[
          'text-[10px] font-bold uppercase',
          highlight ? 'text-red-700' : '',
        ].join(' ')}
      >
        {label}
      </span>
      {subText && (
        <span className="text-[10px] font-extrabold text-red-700 tabular-nums leading-none -mt-0.5">
          {subText}
        </span>
      )}
    </button>
  );
}

export function BottomNavIsland() {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const cart = useSyncExternalStore(subscribe, getStableSnapshot, getStableSnapshot);
  const cartCount = cart.count;
  const cartTotal = cart.total;
  const cartHasItems = cartCount > 0;
  const anyModalOpen = useAnyModalOpen();
  const { t } = useI18n();

  // createPortal pra body — o host (`#delivery-v2-nav-host`) tem
  // `display: none` (host invisível), sem portal a nav herda e fica
  // 0x0. Mesma estratégia dos demais modais.
  return createPortal(
    <nav
      id="delivery-v2-bottom-nav"
      className="fixed bottom-0 z-[60] lg:hidden bg-white border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] transition-transform duration-200"
      style={{
        // Mesmo padrão visual do `#app-wrapper` V1 (max 480px centralizado
        // no desktop). Antes a nav cobria toda largura da viewport e fazia
        // "borrão branco" gigante atrás do app no PC.
        left: '50%',
        // Desliza pra baixo + esconde quando algum modal V2 está aberto —
        // evita stacking com backdrops do cart/upsell/wizard.
        transform: anyModalOpen
          ? 'translateX(-50%) translateY(100%)'
          : 'translateX(-50%) translateY(0)',
        width: '100%',
        maxWidth: 480,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        visibility: anyModalOpen ? 'hidden' : 'visible',
      }}
    >
      <div className="flex justify-around items-center h-[70px] px-2">
        <NavButton
          label={t('Home')}
          icon="fa-home"
          active
          onClick={() => {
            if (typeof window.goBackToHome === 'function') window.goBackToHome();
            else window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
        <NavButton
          label={t('Loja')}
          icon="fa-store"
          onClick={() => window.openStoreInfo?.()}
        />
        <NavButton
          label={t('Sacola')}
          icon="fa-shopping-bag"
          badge={cartCount}
          highlight={cartHasItems}
          subText={cartHasItems ? fmtBRL(cartTotal) : undefined}
          onClick={() => {
            // Sacola vazia: mostra toast/aviso em vez de abrir modal
            // vazio. Antes: clique era no-op silencioso e cliente
            // ficava sem entender (CartModal tinha `if cart=0 return`
            // sem feedback). Toast inline em createElement (sem libs).
            if (!window.CART || window.CART.length === 0) {
              _showEmptyCartToast();
              return;
            }
            window.openCartModal?.();
          }}
        />
        <NavButton
          label={t('Conta')}
          icon="fa-user"
          onClick={() => window.openUserArea?.('orders')}
        />
      </div>
    </nav>,
    document.body,
  );
}
